import type { LucideIcon } from 'lucide-react'
import { RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'

import { cn } from '@/utils/cn'

import { Button } from './Button'
import { Card } from './Card'
import { Skeleton } from './Skeleton'

export type StatTone = 'brand' | 'progress' | 'success' | 'attention' | 'critical' | 'idle'

/** Faixa lateral + ícone na cor do tom. */
const TONES: Record<StatTone, { bar: string; icon: string; iconHover: string }> = {
  brand: { bar: 'bg-brand', icon: 'bg-brand/10 text-brand', iconHover: 'hover:bg-brand/20' },
  progress: {
    bar: 'bg-progress',
    icon: 'bg-progress/12 text-progress',
    iconHover: 'hover:bg-progress/20',
  },
  success: { bar: 'bg-ok', icon: 'bg-ok/12 text-ok', iconHover: 'hover:bg-ok/20' },
  attention: {
    bar: 'bg-attention',
    icon: 'bg-attention/15 text-[#8a6d05] dark:text-attention',
    iconHover: 'hover:bg-attention/25',
  },
  critical: {
    bar: 'bg-critical',
    icon: 'bg-critical/12 text-critical',
    iconHover: 'hover:bg-critical/20',
  },
  idle: { bar: 'bg-idle', icon: 'bg-idle/15 text-idle', iconHover: 'hover:bg-idle/25' },
}

const ICON_FOCUS_CLASS =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'

interface StatCardProps {
  label: string
  value?: string | number
  hint?: string
  icon?: LucideIcon
  /** Cor da faixa lateral e do ícone. */
  tone?: StatTone
  /**
   * Destaca o card quando o valor é diferente de zero (ex.: vencidos). Com tom `critical`
   * o rótulo e o número também ficam em vermelho.
   */
  alerta?: boolean
  /** Quando informado, o ícone vira um link para esta rota. */
  iconTo?: string
  /** Nome acessível do link do ícone (obrigatório na prática quando `iconTo` é usado). */
  iconLabel?: string
  loading?: boolean
  /** Sem fonte de dados ainda (ex.: endpoint de estatísticas pendente). */
  pending?: boolean
  /** Falha ao carregar: mostra o aviso e, com `onRetry`, o botão de nova tentativa. */
  error?: boolean
  onRetry?: () => void
  /** Versão mais discreta (número e ícone menores), para linhas de KPI secundárias. */
  compact?: boolean
  /** Ajusta o número (ex.: fonte menor para valores monetários longos). */
  valueClassName?: string
  className?: string
}

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'brand',
  alerta,
  iconTo,
  iconLabel,
  loading,
  pending,
  error,
  onRetry,
  compact,
  valueClassName,
  className,
}: StatCardProps) {
  const temValor = value !== undefined && value !== 0 && value !== '0'
  const emAlerta = !!alerta && !loading && !error && temValor
  const vermelho = emAlerta && tone === 'critical'
  const colors = TONES[tone]

  return (
    <Card
      className={cn(
        'relative overflow-hidden',
        emAlerta && (tone === 'critical' ? 'border-critical/40' : 'border-attention/50'),
        className,
      )}
    >
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', colors.bar)} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={cn(
              'font-medium',
              compact ? 'text-xs' : 'text-sm',
              vermelho ? 'text-critical' : 'text-fg-muted',
            )}
          >
            {label}
          </p>
          {loading ? (
            <Skeleton className={cn('mt-2 w-20', compact ? 'h-6' : 'h-8')} />
          ) : error ? (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <p className="text-sm text-critical">Erro ao carregar</p>
              {onRetry && (
                <Button variant="ghost" size="sm" onClick={onRetry} className="h-7 px-2">
                  <RefreshCw className="size-3.5" />
                  Tentar novamente
                </Button>
              )}
            </div>
          ) : pending ? (
            <p className={cn('mt-1 font-bold text-fg-subtle', compact ? 'text-xl' : 'text-3xl')}>
              —
            </p>
          ) : (
            <p
              className={cn(
                'mt-1 truncate font-bold tabular-nums',
                compact ? 'text-xl' : 'text-3xl',
                vermelho ? 'text-critical' : 'text-fg',
                valueClassName,
              )}
              title={typeof value === 'string' ? value : undefined}
            >
              {value ?? '—'}
            </p>
          )}
          {hint && !loading && !error && (
            <p className="mt-0.5 text-xs text-fg-subtle">{pending ? 'Aguardando dados' : hint}</p>
          )}
        </div>
        {Icon &&
          (iconTo ? (
            <Link
              to={iconTo}
              aria-label={iconLabel}
              title={iconLabel}
              className={cn(
                'grid shrink-0 place-items-center rounded-xl transition-colors',
                compact ? 'size-9' : 'size-10',
                colors.icon,
                colors.iconHover,
                ICON_FOCUS_CLASS,
              )}
            >
              <Icon className={compact ? 'size-4' : 'size-5'} />
            </Link>
          ) : (
            <span
              className={cn(
                'grid shrink-0 place-items-center rounded-xl',
                compact ? 'size-9' : 'size-10',
                colors.icon,
              )}
            >
              <Icon className={compact ? 'size-4' : 'size-5'} />
            </span>
          ))}
      </div>
    </Card>
  )
}
